import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core'

import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatTableModule, MatTableDataSource } from '@angular/material/table'
import { MatCheckboxModule } from '@angular/material/checkbox'

import { JsonPipe, NgClass, NgIf } from '@angular/common'

import { CnpjPipe } from '../../pipes/cpnj.pipes'
import { CpfPipe } from '../../pipes/cpf.pipes'
import { TelefonePipe } from '../../pipes/phone.pipes'
import { MatProgressSpinner } from '@angular/material/progress-spinner'

interface modelLoading {
  replierItem: boolean
  contract: boolean
  email: boolean
  download: boolean
  edit: boolean
  manager: boolean
  view: boolean
  remove: boolean
  cancelContract: boolean
}

@Component({
  selector: 'table-list-monitoring',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    NgIf,
    CnpjPipe,
    CpfPipe,
    TelefonePipe,
    JsonPipe,
    MatProgressSpinner,
    NgClass,
    MatCheckboxModule,
  ],
  templateUrl: './table-list-monitoring.component.html',
  styleUrl: './table-list-monitoring.component.scss',
})
export class TableListMonitoringComponent implements OnInit, OnChanges {
  constructor() { }

  // Só OS em "Aguardando Confirmação Operador" podem ser confirmadas/recusadas.
  private readonly STATUS_AGUARDANDO_CONFIRMACAO_OPERADOR = 12
  // Só nesses dois status a API de FornecedorGestao aceita atribuir motorista/veículo
  // (11 = Aguardando Fornecedor, 13 = Em Reajuste) — fora disso ela devolve 400.
  private readonly STATUS_AGUARDANDO_FORNECEDOR = 11
  private readonly STATUS_EM_REAJUSTE = 13

  @Input() dataSource: any[] = []
  @Input() columns: any[] = []
  @Input() selectedIds: string[] = []
  @Input() selectionIdField = 'codigoOs'
  @Input() spinLoad: modelLoading = {
    contract: false,
    email: false,
    download: false,
    edit: false,
    manager: false,
    view: false,
    remove: false,
    cancelContract: false,
    replierItem: false,
  }
  @Output() selectItem = new EventEmitter<any>()
  @Output() viewItem = new EventEmitter<any>()
  @Output() downloadItem = new EventEmitter<any>()
  @Output() deleteItem = new EventEmitter<any>()
  @Output() managerItem = new EventEmitter<any>()
  @Output() sendContract = new EventEmitter<any>()
  @Output() sendEmail = new EventEmitter<any>()
  @Output() cancelContract = new EventEmitter<any>()
  @Output() replierItem = new EventEmitter<any>()
  @Output() confirmarOperadorItem = new EventEmitter<any>()
  @Output() recusarOperadorItem = new EventEmitter<any>()
  @Output() toggleSelectItem = new EventEmitter<any>()
  @Output() toggleSelectAll = new EventEmitter<boolean>()
  @Output() viewPassengersItem = new EventEmitter<any>()
  @Output() assignDriverItem = new EventEmitter<any>()
  @Output() assignVehicleItem = new EventEmitter<any>()

  displayedColumns: string[] = []
  tableSource = new MatTableDataSource<any>()

  get selectableRows(): any[] {
    return this.dataSource.filter((item: any) => item.status === this.STATUS_AGUARDANDO_CONFIRMACAO_OPERADOR)
  }

  get allSelected(): boolean {
    return this.selectableRows.length > 0
      && this.selectableRows.every((item: any) => this.selectedIds.includes(item[this.selectionIdField]))
  }

  get someSelected(): boolean {
    return this.selectableRows.some((item: any) => this.selectedIds.includes(item[this.selectionIdField])) && !this.allSelected
  }

  isPendenteOperador(item: any): boolean {
    return item.status === this.STATUS_AGUARDANDO_CONFIRMACAO_OPERADOR
  }

  // fornecedorId é pré-requisito de negócio pra chegar em 11/13 (Compras já atribuiu
  // um fornecedor à OS) — a checagem aqui é só defensiva, pra nunca abrir a modal sem ele.
  canAssignFornecedor(item: any): boolean {
    return (item.status === this.STATUS_AGUARDANDO_FORNECEDOR || item.status === this.STATUS_EM_REAJUSTE)
      && !!item.fornecedorId
  }

  ngOnInit() {
    if (this.dataSource.length > 0) {
      this.displayedColumns = this.columns.map((obj: any) => obj.keyColumn)
      this.tableSource = new MatTableDataSource<any>(this.dataSource)
      this.updateTableDataSource()
    }
  }

  ngOnChanges() {
    this.displayedColumns = this.columns.map((obj: any) => obj.keyColumn)
    this.updateTableDataSource()
  }

  formatTime(time: string) {
    if (time) {
      const arrTime = time.split(':')
      if (arrTime.length > 1) {
        return `${arrTime[0]}:${arrTime[1]}`
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  private updateTableDataSource() {
    this.tableSource.data = this.dataSource
  }

  downloadItemEvent(item: any): void {
    this.downloadItem.emit(item)
  }

  viewItemEvent(item: any): void {
    this.viewItem.emit(item)
  }

  selectItemEvent(item: any): void {
    this.selectItem.emit(item)
  }

  deleteItemEvent(item: any): void {
    this.deleteItem.emit(item)
  }

  sendContractEvent(item: any): void {
    this.sendContract.emit(item)
  }

  sendEmailEvent(item: any): void {
    this.sendEmail.emit(item)
  }

  managerItemEvent(item: any): void {
    this.managerItem.emit(item)
  }

  replierItemEvent(item: any): void {
    this.replierItem.emit(item)
  }

  cancelContractEvent(item: any): void {
    this.cancelContract.emit(item)
  }

  confirmarOperadorEvent(item: any): void {
    this.confirmarOperadorItem.emit(item)
  }

  recusarOperadorEvent(item: any): void {
    this.recusarOperadorItem.emit(item)
  }

  toggleSelectItemEvent(item: any): void {
    this.toggleSelectItem.emit(item)
  }

  toggleSelectAllEvent(checked: boolean): void {
    this.toggleSelectAll.emit(checked)
  }

  viewPassengersEvent(item: any): void {
    this.viewPassengersItem.emit(item)
  }

  assignDriverEvent(item: any): void {
    this.assignDriverItem.emit(item)
  }

  assignVehicleEvent(item: any): void {
    this.assignVehicleItem.emit(item)
  }
}
